import type { DataKey, Database, Entity, ItemStore } from "shelving";
import { getOptionalErrorCode, getSlug, splitEntity } from "shelving";
import { getGroupTitle, getMarkupExcerpt, getProfileTitle } from "shared/schema";
import { useItem } from "../util/db.js";

const EXCERPT_LENGTH = 25;

/**
 * Load and show the name or title of any entity.
 * - For entities (e.g. messages) that don't have a name, show a snippet, e.g. `"Weird how this..."`
 */
export function EntityName({ entity }: { entity: Entity }): React.ReactElement {
	const [type, id] = splitEntity(entity);
	return type === "profile" ? (
		<ProfileName uid={id} />
	) : type === "group" ? (
		<GroupTitle gid={id} />
	) : type === "challenge" ? (
		<ChallengeName id={id} />
	) : type === "reward" ? (
		<RewardName id={id} />
	) : type === "note" ? (
		<NoteName id={id} />
	) : type === "list" ? (
		<ListName id={id} />
	) : type === "tag" ? (
		<HashTagName slug={id} />
	) : type === "post" ? (
		<PostName id={id} />
	) : type === "comment" ? (
		<CommentName id={id} />
	) : type === "message" ? (
		<MessageName id={id} />
	) : (
		<span>{entity}</span> //  Never happens.
	);
}

/** Return something like `private profile` or `deleted group` if the item is loading or has an error. */
export function getNopeReason<T extends Database, K extends DataKey<T>>(store: ItemStore<T, K>): string | undefined {
	const { loading, reason, collection } = store;
	if (loading) return collection;
	if (reason !== undefined) {
		const code = getOptionalErrorCode(reason);
		if (code === "not-found") return `deleted ${collection}`;
		if (code === "permission-denied") return `private ${collection}`;
		throw reason;
	}
	if (!store.exists) return `deleted ${collection}`;
}

export function ProfileName({ uid }: { uid: string }): React.ReactElement {
	const store = useItem("profile", uid);
	return <strong>{getNopeReason(store) || getProfileTitle(store.data)}</strong>;
}

export function GroupTitle({ gid }: { gid: string }): React.ReactElement {
	const store = useItem("group", gid);
	return <strong>{getNopeReason(store) || getGroupTitle(store.data)}</strong>;
}

export function ChallengeName({ id }: { id: string }): React.ReactElement {
	const store = useItem("challenge", id);
	return <strong>{getNopeReason(store) || store.data.name}</strong>;
}

export function RewardName({ id }: { id: string }): React.ReactElement {
	const store = useItem("reward", id);
	return <strong>{getNopeReason(store) || store.data.name}</strong>;
}

export function ListName({ id }: { id: string }): React.ReactElement {
	const store = useItem("list", id);
	return <strong>{getNopeReason(store) || store.data.name}</strong>;
}

export function NoteName({ id }: { id: string }): React.ReactElement {
	const store = useItem("note", id);
	return <strong>{getNopeReason(store) || store.data.name}</strong>;
}

export function PostName({ id, length = EXCERPT_LENGTH }: { id: string; length?: number | undefined }): React.ReactElement {
	const store = useItem("post", id);
	return <strong>{getNopeReason(store) || `"${getMarkupExcerpt(store.data.content, length)}"`}</strong>;
}

export function CommentName({ id, length = EXCERPT_LENGTH }: { id: string; length?: number | undefined }): React.ReactElement {
	const store = useItem("comment", id);
	return <strong>{getNopeReason(store) || `"${getMarkupExcerpt(store.data.content, length)}"`}</strong>;
}

export function MessageName({ id, length = EXCERPT_LENGTH }: { id: string; length?: number | undefined }): React.ReactElement {
	const store = useItem("message", id);
	return <strong>{getNopeReason(store) || `"${getMarkupExcerpt(store.data.content, length)}"`}</strong>;
}

export function HashTagName({ slug: messySlug }: { slug: string }): React.ReactElement {
	const store = getSlug(messySlug);
	return <strong>{`#${store}`}</strong>;
}
